import { GridCustomCellProps } from "@progress/kendo-react-grid";
import "./custom-cells.css";
import { useEffect, useState } from "react";
import { InputBase } from "@mui/material";
import { useTableCellNavigation } from "./use-table-cell-navigation.hook";
import { useBulkChange } from "./use-bulk-change.hook";

export const NumberCustomCell = (props: GridCustomCellProps) => {
	const [input, setInput] = useState<string>("");

	const { handleChange: handleBulkChange } = useBulkChange({
		dataItem: props.dataItem,
		dataIndex: props.dataIndex,
		field: props.field,
		onChange: props.onChange,
	});

	const { handleKeyDown: handleNavigationKeyDown } = useTableCellNavigation({
		field: props.field,
		dataIndex: props.dataIndex,
		focusSelector: (targetField: string, targetRowIndex: number) =>
			`input[data-field="${targetField}"][data-row-index="${targetRowIndex}"]`,
	});

	useEffect(() => {
		if (props.field) {
			setInput(props.dataItem[props.field]?.toString() ?? "");
		}
	}, [props.dataItem, props.field]);

	const applyChange = (event: React.SyntheticEvent<EventTarget, Event>) => {
		if (props.onChange && props.field) {
			const numericValue = Number(input);
			const currentValue = props.dataItem[props.field];
			if (!isNaN(numericValue) && currentValue !== numericValue) {
				props.onChange({
					dataItem: props.dataItem,
					field: props.field,
					syntheticEvent: event,
					value: numericValue,
					dataIndex: props.dataIndex,
				});
			}
		}
	};

	const handleBlur = (
		event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		applyChange(event);
	};

	const handleKeyDown = (
		event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>
	) => {
		if (event.key === "Enter") {
			applyChange(event);
			(event.target as HTMLInputElement).blur();
			return;
		}

		if (event.key === "Backspace" && event.target instanceof HTMLInputElement && event.target.value === "") {
			event.preventDefault();
			handleBulkChange(0, event.metaKey || event.ctrlKey, event.shiftKey, event.altKey);
			return;
		}

		if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
			const input = event.target as HTMLInputElement;
			if (event.key === "ArrowRight") {
				const isAtEnd = input.selectionStart === input.value.length;
				if (!isAtEnd) return;
			} else {
				const isAtStart = input.selectionStart === 0;
				if (!isAtStart) return;
			}
		}

		handleNavigationKeyDown(event);
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value.replace(",", ".");
		if (/^-?\d*[,.]?\d*$/.test(value)) {
			setInput(value);
		}
	};

	return (
		<td className="custom-td">
			<InputBase
				type="text"
				inputMode="decimal"
				data-testid={`number-custom-cell-input-${props.field}`}
				value={input}
				onChange={handleChange}
				onBlur={handleBlur}
				onFocus={(e) => e.target.select()}
				onKeyDown={handleKeyDown}
				fullWidth
				inputProps={{
					"data-field": props.field,
					"data-row-index": props.dataIndex,
				}}
				sx={{
					height: "22px",
					fontSize: "12px",
					paddingLeft: 1,
					"& input": {
						textOverflow: "ellipsis",
						padding: 0,
						textAlign: "right"
					},
					"&:hover": {
						backgroundColor: "rgba(0, 0, 0, 0.04)",
					},
					"&.Mui-focused": {
						backgroundColor: "rgba(0, 0, 0, 0.04)",
					},
					...props.style,
				}}
			/>
		</td>
	);
};
